/**
 * AnticipateConfirmDialog — Diálogo de confirmação reforçado para antecipação
 * de cobrança recorrente (Feature 2 — anti-fraude).
 *
 * Camada E do plano de hardening:
 *  - Exibe Nome do cliente, últimos 4 do cartão padrão (quando disponível),
 *    valor exato, vencimento original vs data atual e dias antecipados.
 *  - Exige digitação manual da palavra "ANTECIPAR" (case-sensitive) para
 *    confirmar — bloqueia clique acidental, double-click e automações.
 *  - Botão de confirmação fica desabilitado até a palavra-chave bater.
 *
 * NÃO chama mutation aqui — apenas dispara onConfirm() quando o operador
 * confirma com a palavra-chave. A mutation real (incluindo auditoria via
 * RPC `log_subscription_anticipate_event`) é orquestrada pelo componente pai.
 *
 * RELEASE: 2026-06-21 v1.0.0 — Versão inicial.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, Rocket, ShieldAlert, CreditCard, User, CalendarDays } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InlineNotice } from '@/components/ui/inline-notice';

/** Palavra-chave que o operador precisa digitar — case-sensitive, sem ambiguidade. */
const CONFIRM_KEYWORD = 'ANTECIPAR';

/** Snapshot mínimo do cartão padrão a ser cobrado (apenas dados seguros). */
export interface AnticipateCardInfo {
  brand?: string | null;          // ex.: 'VISA'
  last4?: string | null;          // últimos 4 dígitos
  holder_masked?: string | null;  // ex.: 'MARIA S.' (já mascarado server-side)
}

interface AnticipateConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Disparado apenas após o operador digitar a palavra-chave corretamente. */
  onConfirm: () => void;
  isPending?: boolean;

  // ─── Contexto para o operador conferir antes de confirmar ───
  customerName?: string | null;
  /** Valor exato em reais (centavos NÃO — já em BRL decimal). */
  valor: number;
  moeda?: string;
  /** Vencimento original do ciclo (ISO YYYY-MM-DD ou completo). */
  dataVencimentoOriginal?: string | null;
  /** Cartão padrão da recorrência (quando aplicável). */
  cardInfo?: AnticipateCardInfo | null;
  /** Número do ciclo, para contexto visual. */
  cicloNumero?: number | null;
}

/** Calcula a diferença em dias entre hoje e o vencimento original. */
function calcDiasAntecipados(dataVencimentoIso: string | null | undefined): number | null {
  if (!dataVencimentoIso) return null;
  const ymd = dataVencimentoIso.substring(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!ymd) return null;
  const [, y, m, d] = ymd;
  const venc = new Date(Number(y), Number(m) - 1, Number(d));
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const diffMs = venc.getTime() - hoje.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
}

export function AnticipateConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending = false,
  customerName,
  valor,
  moeda = 'BRL',
  dataVencimentoOriginal,
  cardInfo,
  cicloNumero,
}: AnticipateConfirmDialogProps) {
  const [typed, setTyped] = useState('');

  // Reseta o campo sempre que o diálogo fechar/abrir — evita vazamento entre cobranças.
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const dias = useMemo(() => calcDiasAntecipados(dataVencimentoOriginal), [dataVencimentoOriginal]);
  const valorFmt = useMemo(
    () => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: moeda }).format(valor || 0),
    [valor, moeda],
  );
  const vencFmt = useMemo(() => {
    if (!dataVencimentoOriginal) return '—';
    const ymd = dataVencimentoOriginal.substring(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!ymd) return dataVencimentoOriginal;
    const [, y, m, d] = ymd;
    return format(new Date(Number(y), Number(m) - 1, Number(d)), 'dd/MM/yyyy', { locale: ptBR });
  }, [dataVencimentoOriginal]);
  const hojeFmt = format(new Date(), 'dd/MM/yyyy', { locale: ptBR });

  // Match exato (case-sensitive, sem espaços) — não aceitamos variações.
  const isConfirmed = typed === CONFIRM_KEYWORD;
  const canSubmit = isConfirmed && !isPending;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-primary" />
            Confirmar antecipação de cobrança
            {cicloNumero != null && (
              <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-primary/10 text-primary">
                Ciclo #{cicloNumero}
              </span>
            )}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              {/* Bloco de conferência: dados sensíveis em destaque visual */}
              <div className="rounded-md border border-border bg-muted/40 p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <User className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Cliente</p>
                    <p className="text-foreground font-medium truncate">{customerName || '—'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <span className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground flex items-center justify-center font-bold">$</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Valor a ser cobrado</p>
                    <p className="text-foreground font-semibold">{valorFmt}</p>
                  </div>
                </div>

                {cardInfo && (cardInfo.last4 || cardInfo.brand) && (
                  <div className="flex items-start gap-2">
                    <CreditCard className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Cartão a ser debitado</p>
                      <p className="text-foreground">
                        <span className="font-medium">{(cardInfo.brand || 'Cartão').toUpperCase()}</span>
                        {cardInfo.last4 && <span className="ml-1 font-mono">•••• {cardInfo.last4}</span>}
                        {cardInfo.holder_masked && <span className="ml-1 text-muted-foreground">· {cardInfo.holder_masked}</span>}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-2">
                  <CalendarDays className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Datas</p>
                    <p className="text-foreground text-xs">
                      Hoje <span className="font-medium">{hojeFmt}</span> · Vencimento original{' '}
                      <span className="font-medium">{vencFmt}</span>
                      {dias != null && dias > 0 && (
                        <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                          {dias} {dias === 1 ? 'dia antecipado' : 'dias antecipados'}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <InlineNotice variant="warning" icon={ShieldAlert}>
                Esta ação debita o cliente imediatamente e não pode ser desfeita pelo sistema.
                Toda antecipação é auditada com seu usuário, IP e horário.
              </InlineNotice>

              {/* Campo de digitação obrigatória — guarda anti-fraude */}
              <div className="space-y-1.5">
                <Label htmlFor="antecipar-confirm" className="text-xs">
                  Digite <span className="font-mono font-bold text-foreground">{CONFIRM_KEYWORD}</span> para liberar a confirmação
                </Label>
                <Input
                  id="antecipar-confirm"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={CONFIRM_KEYWORD}
                  className="font-mono"
                  disabled={isPending}
                />
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            disabled={!canSubmit}
            onClick={(e) => {
              e.preventDefault();
              if (!canSubmit) return;
              onConfirm();
            }}
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Antecipando...
              </>
            ) : (
              <>
                <Rocket className="h-4 w-4 mr-2" />
                Sim, cobrar agora
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
